import { useEffect } from 'react';

import { TIER_TO_TRACK_CODE } from '@backrs_ui/common-ui';

import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { updateSharedBlueprintData } from '../redux/sharedSession/sharedSession';
import { useGetMostRecentBlueprintDataQuery } from '../redux/sharedSession/sharedSession.api';
import { getSurveyResults } from '../redux/userResponse/thunks/userResponse.thunk';
import { determineIfEntrepreneurshipRecommended } from '../utils/determineEntrepreneurship';

type UseSharedBlueprintDataProps = Readonly<{
  accessToken: string;
  skip?: boolean;
}>;

export const useSharedBlueprintData = ({
  accessToken,
  skip = false,
}: UseSharedBlueprintDataProps) => {
  const dispatch = useAppDispatch();

  const {
    data: mostRecentBlueprintData,
    isError,
    isLoading,
    error,
  } = useGetMostRecentBlueprintDataQuery({ accessToken }, { skip: !accessToken || skip });

  const careerQuestions = useAppSelector((state) => state.survey.careerQuestions);
  const { careerPaths, careerPathRoles, careerPathMeanings, careerPathEnjoyments } = useAppSelector(
    (state) => state.careerPath,
  );

  useEffect(() => {
    // 200 with empty payload doesn't trigger isError — log so it's visible in prod
    if (mostRecentBlueprintData && !mostRecentBlueprintData.userResponseSet) {
      console.warn('Shared blueprint payload missing userResponseSet', mostRecentBlueprintData);

      return;
    }

    if (mostRecentBlueprintData?.userResponseSet) {
      const surveyResults = getSurveyResults({
        userResponseSet: mostRecentBlueprintData?.userResponseSet,
        careerQuestions,
        careerPaths,
        careerPathRoles,
        careerPathMeanings,
        careerPathEnjoyments,
      });

      const entrepreneurshipRecommended = determineIfEntrepreneurshipRecommended(
        mostRecentBlueprintData.userResponseSet,
        careerQuestions,
      );

      dispatch(
        updateSharedBlueprintData({
          tier: surveyResults.tier,
          careerPathSuggestions: surveyResults.careerPathSuggestions,
          trackCode: TIER_TO_TRACK_CODE[surveyResults.tier],
          userResponseSet: mostRecentBlueprintData.userResponseSet,
          entrepreneurshipRecommended,
          aspirationsCategories: mostRecentBlueprintData.aspirations_categories,
        }),
      );
    }
  }, [
    mostRecentBlueprintData,
    careerQuestions,
    careerPaths,
    careerPathRoles,
    careerPathMeanings,
    careerPathEnjoyments,
    dispatch,
  ]);

  return {
    mostRecentBlueprintData,
    isError,
    isLoading,
    error,
  };
};
